if (!customElements.get('product-reassurance')) {
  class ProductReassurance extends HTMLElement {
    static SELECTORS = {
      slide: '[data-reassurance-slide]',
      dot: '[data-reassurance-dot]',
      date: '[data-reassurance-date]',
    };

    static CLASSES = {
      slideActive: 'product-reassurance__slide--active',
      slideLeaving: 'product-reassurance__slide--leaving',
      dotActive: 'product-reassurance__dot--active',
    };

    static ATTRS = {
      dotIndex: 'data-reassurance-dot',
    };

    static DEFAULTS = {
      intervalMs: 3000,
      minIntervalMs: 1000,
    };

    /** Must match the transition duration in custom.css (.product-reassurance__slide). */
    static TRANSITION_MS = 400;

    static DATE = {
      fallbackLocale: 'fr-FR',
      timeZone: 'Europe/Paris',
      isoLocale: 'en-CA',
      format: { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' },
    };

    /**
     * The page only exposes a bare language code (`<html lang="en">`), and bare `en`
     * formats US-style ("Friday, October 2"). Pin English to the day-first order the
     * rest of Europe uses so it reads like the French version.
     */
    static LOCALE_BY_LANG = { en: 'en-GB' };

    /**
     * Days to add to today to get the announced delivery day, keyed by ISO weekday
     * (1 = Monday … 7 = Sunday). Mon–Thu ship for +2, Fri/Sat for the following
     * Tuesday/Wednesday, Sunday for Wednesday.
     */
    static DELIVERY_OFFSET_BY_WEEKDAY = { 1: 2, 2: 2, 3: 2, 4: 2, 5: 4, 6: 4, 7: 3 };

    static MS_PER_DAY = 86400000;

    connectedCallback () {
      this._slides = Array.from(this.querySelectorAll(ProductReassurance.SELECTORS.slide));
      if (this._slides.length === 0) return;

      this._dots = Array.from(this.querySelectorAll(ProductReassurance.SELECTORS.dot));
      this._abort = new AbortController();
      this._intervalMs = Math.max(
        this._toInt(this.dataset.interval, ProductReassurance.DEFAULTS.intervalMs),
        ProductReassurance.DEFAULTS.minIntervalMs
      );
      this._index = 0;
      this._timer = null;
      this._leaveTimer = null;

      this._fillDeliveryDate();
      this._activate(this._index);

      if (this._slides.length < 2) return;

      this._bindDots();
      this._bindVisibility();
      this._start();
    }

    disconnectedCallback () {
      this._stop();
      clearTimeout(this._leaveTimer);
      this._abort?.abort();
    }

    _bindDots () {
      const { signal } = this._abort;

      this._dots.forEach((dot) => {
        const index = this._toInt(dot.getAttribute(ProductReassurance.ATTRS.dotIndex), 0);
        dot.addEventListener('click', () => this._select(index), { signal });
      });
    }

    _bindVisibility () {
      document.addEventListener(
        'visibilitychange',
        () => (document.hidden ? this._stop() : this._start()),
        { signal: this._abort.signal }
      );
    }

    _start () {
      if (document.hidden) return;
      this._stop();
      this._timer = setInterval(() => this._next(), this._intervalMs);
    }

    _stop () {
      if (!this._timer) return;
      clearInterval(this._timer);
      this._timer = null;
    }

    _next () {
      this._goTo((this._index + 1) % this._slides.length);
    }

    /** A manual pick also restarts the clock so the next auto-advance is a full interval away. */
    _select (index) {
      this._goTo(index);
      this._start();
    }

    _goTo (index) {
      if (index === this._index) return;

      this._leave(this._slides[this._index]);
      this._index = index;
      this._activate(this._index);
    }

    _activate (index) {
      this._activateSlide(index);
      this._activateDot(index);
    }

    _activateSlide (index) {
      const { slideActive, slideLeaving } = ProductReassurance.CLASSES;

      this._slides.forEach((slide, position) => {
        const isActive = position === index;
        slide.classList.toggle(slideActive, isActive);
        if (isActive) slide.classList.remove(slideLeaving);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });
    }

    _activateDot (index) {
      const { dotActive } = ProductReassurance.CLASSES;

      this._dots.forEach((dot, position) => {
        const isActive = position === index;
        dot.classList.toggle(dotActive, isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    /**
     * Sends the outgoing slide up and out, then parks it back below the viewport
     * (its resting state) once the transition has finished, so it can re-enter from
     * the bottom next time round.
     */
    _leave (slide) {
      const { slideLeaving } = ProductReassurance.CLASSES;

      clearTimeout(this._leaveTimer);
      slide.classList.add(slideLeaving);
      this._leaveTimer = setTimeout(
        () => slide.classList.remove(slideLeaving),
        ProductReassurance.TRANSITION_MS
      );
    }

    _fillDeliveryDate () {
      const targets = this.querySelectorAll(ProductReassurance.SELECTORS.date);
      if (targets.length === 0) return;

      const label = this._formatDeliveryDate(this._deliveryDate());
      targets.forEach((target) => {
        target.textContent = label;
      });
    }

    /**
     * Today in the shop's timezone, normalised to UTC midnight so that day
     * arithmetic and formatting never drift across DST boundaries.
     */
    _todayInShopTimeZone () {
      const { isoLocale, timeZone } = ProductReassurance.DATE;
      const iso = new Date().toLocaleDateString(isoLocale, {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });

      const [year, month, day] = iso.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    }

    _deliveryDate () {
      const today = this._todayInShopTimeZone();
      const isoWeekday = today.getUTCDay() === 0 ? 7 : today.getUTCDay();
      const offset = ProductReassurance.DELIVERY_OFFSET_BY_WEEKDAY[isoWeekday];

      return new Date(today.getTime() + offset * ProductReassurance.MS_PER_DAY);
    }

    _formatDeliveryDate (date) {
      return date.toLocaleDateString(this._dateLocale(), ProductReassurance.DATE.format);
    }

    /** Follows the page language (request.locale) so the date reads in the visitor's language. */
    _dateLocale () {
      const lang = document.documentElement.lang || ProductReassurance.DATE.fallbackLocale;
      return ProductReassurance.LOCALE_BY_LANG[lang] || lang;
    }

    _toInt (value, fallback) {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? fallback : parsed;
    }
  }

  customElements.define('product-reassurance', ProductReassurance);
}
